<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { ErrorKind, ErrorSource, ErrorTier, type NormalizedError } from '@/errors';
import { useErrorStore } from '@/stores/error';
import ErrorState from './ErrorState.vue';

/**
 * Catches render errors from its subtree instead of letting one broken component blank the app.
 *
 * `onErrorCaptured` returning `false` stops propagation, so `app.config.errorHandler` never sees the
 * error and it is reported exactly once — that is half of the double-report fix. The exception is the
 * path where reporting itself failed: that one propagates, because an error nobody reported is worse
 * than an error reported twice.
 */
defineProps({
  level: {
    type: String,
    default: 'page'
  }
});

const errorStore = useErrorStore();
const route = useRoute();

const failed = ref<NormalizedError | undefined>();
/** Bumped to remount the subtree, so a retry gets fresh component state rather than the broken one. */
const subtreeKey = ref(0);
/** If the fallback itself throws, drop to plain text with no child components. */
const fallbackFailed = ref(false);

/**
 * Vue tells us where the error came from. What matters is whether the subtree is structurally broken
 * or merely misbehaved: a click handler throwing should toast, not replace the page.
 */
const STRUCTURAL_INFO = ['setup function', 'render function', 'scheduler flush', 'nextTick'];
const isStructural = (info: string) =>
  STRUCTURAL_INFO.some(needle => info.includes(needle)) || info.includes('hook');

onErrorCaptured((error, instance, info) => {
  try {
    const structural = isStructural(info);
    const normalized = errorStore.handle(error, {
      source: ErrorSource.RENDER,
      // The kind picks the copy the user reads, and `error_render_*` says "This screen could not load".
      // True of a broken setup or render; wrong for a click handler that threw on a screen sitting
      // there working. Those get the generic copy, and `source` still says where it came from.
      kind: structural ? ErrorKind.RENDER : ErrorKind.UNKNOWN,
      tier: structural ? ErrorTier.INLINE : ErrorTier.TOAST
    });
    if (structural) {
      failed.value = normalized;
    }
  } catch {
    fallbackFailed.value = true;
    // Propagate. Stopping here on the one path where we did *not* report the error would lose it
    // entirely — no enclosing boundary, no `app.config.errorHandler`, no Sentry event, just the
    // fallback sentence. Only `false` stops propagation, so this hands the error back to Vue.
    return true;
  }
  return false;
});

const reset = () => {
  failed.value = undefined;
  fallbackFailed.value = false;
  subtreeKey.value += 1;
};

/**
 * Essential while a fallback is on screen: without it one bad render traps the user here forever,
 * including after "Go to home". Guarded, because `reset` also bumps the remount key — resetting a
 * healthy subtree would tear down and rebuild the whole page on every navigation, query-only ones
 * included (opening the pipeline's entry dialog is exactly that), losing the state of the view the
 * user is still looking at.
 */
watch(() => route.fullPath, () => {
  if (failed.value || fallbackFailed.value) {
    reset();
  }
});

defineExpose({ reset });
</script>

<template>
  <div class="error-boundary-fallback" v-if="fallbackFailed">
    Something went wrong and we could not display the details. Please reload the page.
  </div>
  <ErrorState v-else-if="failed"
    :error="failed"
    :compact="level === 'panel'"
    @retry="reset"
    @reload="errorStore.reloadApp()"
    @report="errorStore.openReport(failed)"/>
  <!-- The remount key sits on a real element rather than on the `<slot>`. Keying the slot did work —
       `renderSlot` promotes a `key` prop to the fragment's key — but it leans on an internal detail and
       also hands `key` to the slot content as an ordinary prop. `display: contents` keeps the wrapper
       out of the layout, so a boundary around a flex or grid child behaves as it did without one. -->
  <div v-else :key="subtreeKey" class="error-boundary-subtree">
    <slot></slot>
  </div>
</template>

<style scoped lang="scss">
.error-boundary-subtree {
  display: contents;
}

.error-boundary-fallback {
  padding: 24px;
  text-align: center;
  font-size: 14px;
  color: #6b7075;
}
</style>
